import { IProject, StoredRecord } from 'models';
import {
  Loading,
  Card,
  Popup,
  Component,
  register,
  Typography,
  Button,
  StyledIcon,
} from 'rewild-ui';
import { NewProjectForm } from '../NewProjectForm';
import { projectsStore } from '../../../stores/ProjectsStore';
import { ProjectStore } from 'src/ui/stores/ProjectStore';
import { confirmationStore } from '../../../stores/ConfirmationStore';
import { authStore } from '../../../stores/AuthStore';
import { isSavedOnServer } from 'src/database/database';
import { ProjectImage } from '../../common/ProjectImage';

interface Props {
  open: boolean;
  onBack: () => void;
  onOpen: (projectUid: string) => void;
}

@register('x-project-selector')
export class ProjectSelector extends Component<Props> {
  init() {
    this.on(projectsStore.dispatcher);
    this.on(authStore.dispatcher);

    // A project saved on the server is deleted there, which needs a login.
    const canDelete = (project: StoredRecord<IProject>) =>
      !isSavedOnServer(project) || authStore.loggedIn;

    const isPublic = (project: StoredRecord<IProject>) =>
      project.id === projectsStore.publicProjectId;

    const onCreate = async () => {
      await projectsStore.addProject(newProject()!);
      setNewProject(null);
    };

    const onDelete = () => {
      const project = selectedProject()!;
      confirmationStore.show(
        'Delete Project',
        `Are you sure you want to delete "${project.name}"? This action cannot be undone.`,
        async () => {
          if (await projectsStore.deleteProject(project.id))
            setSelectedProject(null);
        },
        { okLabel: 'Delete', okColor: 'error' }
      );
    };

    const onBack = () => {
      if (newProject()) setNewProject(null);
      else this.props.onBack();
    };

    const isProjectValid = () => {
      if (!newProject()?.name) return false;
      return true;
    };

    const onNewProject = () => {
      setNewProject(ProjectStore.createProject());
      setSelectedProject(null);
    };

    this.onMount = () => {
      projectsStore.getProjects();
    };

    const [newProject, setNewProject] = this.useState<Partial<IProject> | null>(
      null
    );
    const [selectedProject, setSelectedProject] =
      this.useState<StoredRecord<IProject> | null>(null);

    return () => {
      const isProjectSelected = !!selectedProject();
      const isNewProject = !!newProject();
      const props = this.props;
      const loading = projectsStore.loading;

      return (
        <Popup open={props.open}>
          <div class="container">
            <div class="nav-buttons">
              <Button variant="text" onClick={onBack}>
                <StyledIcon icon="chevron-left" />
                <span>Back</span>
              </Button>
            </div>
            <div>
              {isProjectSelected && (
                <Typography variant="h4">{selectedProject()!.name}</Typography>
              )}
            </div>

            <div class="projects">
              {isNewProject ? (
                [
                  <NewProjectForm
                    project={newProject()}
                    onChange={(project) => setNewProject(project, false)}
                  />,
                  !!projectsStore.error && projectsStore.error,
                ]
              ) : (
                <div class="projects-list">
                  <Card button raised disabled={loading} onClick={onNewProject}>
                    {/* Flexed on the Typography's own box rather than around it:
                        the label wraps at this card width, and a row here keeps
                        the icon centred against the whole wrapped block. */}
                    <Typography
                      variant="h4"
                      style="display: flex; align-items: center; justify-content: center; gap: 5px">
                      <StyledIcon icon="circle-plus" size="s" />
                      <span>New Project</span>
                    </Typography>
                  </Card>
                  {loading || projectsStore.error ? (
                    projectsStore.error ? (
                      projectsStore.error
                    ) : (
                      <Loading />
                    )
                  ) : undefined}
                  {!loading
                    ? projectsStore.projects.map((item) => (
                        <Card
                          button
                          raised
                          css={ProjectCardOverrides}
                          onClick={(e) => setSelectedProject(item)}
                          pushed={selectedProject()?.id === item.id}>
                          <div class="project-card">
                            <ProjectImage project={item} />
                            <div class="project-name">
                              <Typography variant="label">{item.name}</Typography>
                            </div>
                          </div>
                        </Card>
                      ))
                    : null}
                </div>
              )}
            </div>

            <div>
              {isProjectSelected && [
                <Typography variant="label">Description</Typography>,
                <Typography variant="body1">
                  {selectedProject()!.description}
                </Typography>,
              ]}
            </div>

            <div class="actions">
              {isProjectSelected && canDelete(selectedProject()!) && (
                <Button
                  color="error"
                  variant="text"
                  fullWidth
                  disabled={isPublic(selectedProject()!)}
                  onClick={onDelete}>
                  Delete
                </Button>
              )}
              {isProjectSelected && isPublic(selectedProject()!) && (
                <Typography variant="info">
                  This is the public game. An admin must remove it from Story
                  Mode before it can be deleted.
                </Typography>
              )}

              {isProjectSelected && (
                <Button
                  onClick={(e) => props.onOpen(selectedProject()!.id!)}
                  fullWidth>
                  Open
                </Button>
              )}

              {isNewProject && (
                <Button
                  onClick={onCreate}
                  fullWidth
                  disabled={loading || !isProjectValid()}>
                  Create Project
                </Button>
              )}
            </div>
          </div>
        </Popup>
      );
    };
  }

  getStyle() {
    return css`
      .container {
        display: grid;
        align-content: stretch;
        grid-template-columns: 2fr 1fr;
        grid-template-rows: 50px 1fr 1fr;
        width: 860px;
        height: 500px;
        min-height: 300px;
      }

      .projects {
        grid-row-start: 2;
        grid-row-end: 4;
      }

      .projects .projects-list {
        overflow: auto;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        grid-gap: 10px 0;
      }

      .projects x-card {
        width: 160px;
        height: 160px;
        text-align: center;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        transition: box-shadow 0.25s;
        margin: 2px;
      }

      .project-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
      }

      .project-card x-project-image {
        flex: 1;
        min-height: 0;
      }

      .project-name {
        padding: 0.5rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .actions {
        align-self: end;
      }

      .actions x-button {
        margin-top: 10px;
      }

      .nav-buttons x-button {
        margin-bottom: 10px;
      }
    `;
  }
}

const ProjectCardOverrides = css`
  :host {
    padding: 0;
    overflow: hidden;
  }
`;
